// src/modules/search/store/searchStore.js
import {defineStore} from 'pinia';
import api from "@/core/services/axios.js";
import {
    createPaginationState,
    createSearchHistoryEntry,
    isNewHistoryEntry
} from "@/modules/search/utils/cursorUtils.js";

export const useSearchStore = defineStore('search', {
    state: () => ({
        items: [],
        pagination: {
            next_cursor: null,
            prev_cursor: null,
            has_next: false,
            has_prev: false,
            current_cursor: null,
            total_count: 0,
            current_page: 1,
            total_pages: 1,
        },
        isLoading: false,
        error: null,
        searchHistory: [],
        currentSearchIndex: 0,
        suggestions: [],
    }),
    getters: {
        canGoNext: (state) => {
            return state.pagination.has_next === true;
        },
        canGoPrev: (state) => {
            return state.pagination.has_prev === true;
        },
    },
    actions: {
        async performSearch(query, category = 'all', options = {}) {
            try {
                this.isLoading = true;
                this.error = null;

                // Backend uses offset-based pagination with 'page' parameter
                const params = {
                    q: query,
                    type: category !== 'all' ? category : undefined,
                    limit: options.limit || 20,  // Default limit matches backend
                    page: options.page || 1,      // Page number for offset-based pagination
                };

                // Use correct backend endpoint: /listings instead of /search
                const response = await api.get('/listings', {params});

                if (response?.data) {
                    const data = response.data;

                    if (Array.isArray(data.data)) {
                        this.items = data.data;
                    } else {
                        this.items = [];
                    }

                    // Backend returns offset-based pagination fields
                    this.pagination = {
                        current_page: data.current_page || 1,
                        total_pages: data.total_pages || 1,
                        limit: data.limit || 20,
                        total_count: data.total_count || 0,
                        has_next: (data.current_page || 1) < (data.total_pages || 1),
                        has_prev: (data.current_page || 1) > 1,
                    };

                    const newEntry = createSearchHistoryEntry(query, category, data.current_page);
                    const lastEntry = this.searchHistory[this.searchHistory.length - 1];

                    if (isNewHistoryEntry(newEntry, lastEntry)) {
                        this.searchHistory.push(newEntry);
                        this.currentSearchIndex = this.searchHistory.length - 1;
                    }
                }

                return response;
            } catch (error) {
                console.error('Search error:', error);
                this.error = error;
                this.items = [];
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        async searchSuggestions(query, category = 'all') {
            try {
                const params = {
                    q: query,
                    type: category !== 'all' ? category : undefined,
                    limit: 10,
                };

                // Use correct backend endpoint: /listings instead of /search/suggestions
                const response = await api.get('/listings', {params});
                this.suggestions = response?.data?.data || response?.data || [];
                return response;
            } catch (error) {
                console.error('Suggestions error:', error);
                this.suggestions = [];
                throw error;
            }
        },

        async loadNextPage(query, category = 'all', options = {}) {
            if (!this.canGoNext) return;

            try {
                this.isLoading = true;

                const nextPage = (this.pagination.current_page || 1) + 1;
                const response = await this.performSearch(query, category, {
                    ...options,
                    page: nextPage,
                });

                return response;
            } catch (error) {
                console.error('Load next page error:', error);
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        async loadPrevPage(query, category = 'all', options = {}) {
            if (!this.canGoPrev) return;

            try {
                this.isLoading = true;

                const prevPage = Math.max(1, (this.pagination.current_page || 1) - 1);
                const response = await this.performSearch(query, category, {
                    ...options,
                    page: prevPage,
                });

                return response;
            } catch (error) {
                console.error('Load prev page error:', error);
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        resetSearch() {
            this.items = [];
            this.pagination = {
                next_cursor: null,
                prev_cursor: null,
                has_next: false,
                has_prev: false,
                current_cursor: null,
                total_count: 0,
                current_page: 1,
                total_pages: 1,
            };
            this.error = null;
            this.searchHistory = [];
            this.currentSearchIndex = 0;
        },

        clearState() {
            this.items = [];
            this.pagination = {
                next_cursor: null,
                prev_cursor: null,
                has_next: false,
                has_prev: false,
                current_cursor: null,
                total_count: 0,
                current_page: 1,
                total_pages: 1,
            };
            this.isLoading = false;
            this.error = null;
            this.suggestions = [];
        },
    },
});
